import React, { useState } from "react";
import { ArrowOutward, NotificationImportant, WarningAmber } from '@mui/icons-material';
import { Box, Paper, Typography, Popover } from '@mui/material';
import { useNavigate } from "react-router-dom";
import RenderSvg from "../renderSvg/RenderSvg";
import "./DayEndNavigator.css";
import CustomGenericModal from "./CustomGenericModal";
import { GenericModal, initialGenericModalProps } from "../../features/viewOrder/ViewOrderUtils";
import { getGenericModalProps } from "../loadouts/LoadoutUtils";
import { completeDayEnd, getDayEndStatusFromSession, updateDayEndStatusInSession, updateLasteEndDate } from "./DayEndUtils";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";
import { safeStringify } from "./DmsUtils";

interface styleObj{
    textStyle: React.CSSProperties
    svgStyle: React.CSSProperties
    buttonStyle: React.CSSProperties
}

interface TaskObjectItem{
    svgIcon: string;
    style: styleObj
}

interface TaskObject{
    invoices: TaskObjectItem;
    loadouts: TaskObjectItem;
    deliveryConfirmation: TaskObjectItem
    completeDayEnd: TaskObjectItem
}

export default function DayEndNavigator({ currentPage }: { currentPage: string }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [genericModalProps, setGenericModalProps] =
    useState<GenericModal>(initialGenericModalProps);


  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (open) return;
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleNav = (id: string) => {
    if (id === "invoices") {
      if (currentPage === "invoices") return;
      navigate('/dashboard/sale/manageOrders');
    } else if (id === "loadouts") {
      if (currentPage === "loadouts") return;
      navigate('/dashboard/delivery/loadouts/preseller');
    } else if (id === "deliveryConfirmation") {
      if (currentPage === "deliveryConfirmation") return;
      navigate('/dashboard/manageLoadout');
    }
    handlePopoverClose();
  }

  const currentItem: TaskObjectItem = {
    svgIcon: "stopWatchSVG",
    style: {
      textStyle: { color: "#CD8226", fontWeight: 600, fontSize: "0.8em" },
      svgStyle: { stroke: "#CD8226", width: "1em" },
      buttonStyle: {
        backgroundColor: "#FFEDD6",
        border: "1px solid #FED7AA",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        borderRadius: "12px",
        padding: "6px 6px",
        whiteSpace: "nowrap"
      }
    }
  }

  const otherItem: TaskObjectItem = {
    svgIcon: "disclaimerIcon",
    style: {
      textStyle: { color: "#FFFFFF", fontWeight: 600, fontSize: "0.8em" },
      svgStyle: { 
        color: "#B54747", width: "1em"
      },
      buttonStyle: {
        backgroundColor: "#F87171",
        border: "1px solid #F87171",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        borderRadius: "12px",
        padding: "6px 6px",
        whiteSpace: "nowrap"
      }
    }
  }

  const canMarkDayEnd: TaskObjectItem = {
    svgIcon: "invoicedOrdersSvg",
    style:{
      textStyle: {color: "white", fontWeight: 600, fontSize: "0.8em"},
      svgStyle: {
        color: "white",
        width: "1em"
      },
      buttonStyle: {
        backgroundColor: "#00a392",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        borderRadius: "12px",
        padding: "6px",
        whiteSpace: "nowrap"
      }
    }
  }

  const dayEndObj = getDayEndStatusFromSession();

  const markDayEnd = async () => {
    await updateDayEndStatusInSession()
    const newDayEndObj = getDayEndStatusFromSession();
    if (newDayEndObj && !newDayEndObj.canMarkDayEnd) {
      setGenericModalProps(getGenericModalProps(
        "positive",
        "Please complete all the activites"
      ))
      return;
    }

    //update last day end in table
    try{

      await updateLasteEndDate()
      setGenericModalProps(getGenericModalProps(
        "success",
        "Day End marked successfully",
        "Continue",
        "",
        () => {
          resetGenericModalProps();
          navigate("/dashboard/sale/manageOrders");
        },
      ))
      await completeDayEnd();
    } catch (error: any) {
      if (error?.message === "alreadyMarked"){
        setGenericModalProps(getGenericModalProps(
          "negative",
          "Day end already marked"
        ))
        await completeDayEnd();
      } else {
        setGenericModalProps(getGenericModalProps(
          "negative",
          "Failed to mark Day End. Please try again."
        ))
      }
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Errow while marking dayend",
        `Error: ${(error as Error).message}`,
        safeStringify({ error })
      );
      console.error(error);
    }
  }

  const generateTaskObject = (): TaskObject => {
    return {
      invoices: currentPage === "invoices" ? currentItem : otherItem,
      loadouts: currentPage === "loadouts" ? currentItem : otherItem,
      deliveryConfirmation: currentPage === "deliveryConfirmation" ? currentItem : otherItem,
      completeDayEnd: canMarkDayEnd 
    }
  }

  const taskObject: TaskObject = generateTaskObject();

  const statusesAll = [
    //If i have provide loadout creation that is action on invoice then only skipCheck should be true
    {
      icon: taskObject.invoices.svgIcon,
      text: "Pending Invoices",
      id: "invoices",
      status: "PENDING",
      styles: taskObject.invoices.style,
      onClick: () => handleNav("invoices"),
      skipCheck: (dayEndObj && dayEndObj.mandatoryStatus.includes("INVOICED") ? true : false)
    },
    {
      icon: taskObject.loadouts.svgIcon,
      id: "loadouts",
      text: "Loadout Creation",
      status: "INVOICED",
      styles: taskObject.loadouts.style,
      onClick: () => handleNav("loadouts"),
    },
    {
      icon: taskObject.deliveryConfirmation.svgIcon,
      id: "deliveryConfirmation",
      text: "Delivery Confirmation",
      status: "OUT FOR DELIVERY",
      styles: taskObject.deliveryConfirmation.style,
      onClick: () => handleNav("deliveryConfirmation"),
    },
    {
      icon: "invoicedOrdersSVG",
      id: "completeDayEnd",
      text: "Mark Day End",
      status: "",
      styles: taskObject.completeDayEnd.style,
      onClick: () => markDayEnd(),
      skipCheck: true
    }
  ];

  const statuses = statusesAll.filter((s) => {
    if (s.skipCheck) return true
    if (!dayEndObj) return false
    return dayEndObj.mandatoryStatus.includes(s.status.toUpperCase())
  })

  const resetGenericModalProps = () => setGenericModalProps(initialGenericModalProps);


  return (
    <>

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        rightButtonText={genericModalProps?.rightButtonText}
        modalOpen={genericModalProps?.open}
        onLeftButtonClick={genericModalProps?.onLeftButtonClick}
        onRightButtonClick={genericModalProps?.onRightButtonClick}
        leftButtonPadding={genericModalProps?.leftButtonPadding}
        onClose={resetGenericModalProps}
        width={genericModalProps?.width}
      />
      {(!dayEndObj || (!dayEndObj.isDayEndPending && !dayEndObj.isDayEndInProgress)) ?
        <></>
      :
      (
        <>
          <Box
            onMouseEnter={handlePopoverOpen}
            onClick={() => {}}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              padding: 1,
              backgroundColor: "#F87171",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#EF4444",
                transform: "scale(1.05)",
              }
            }}
          >
            <WarningAmber sx={{ color: "#FFFFFF", fontSize: 24 }} />
          </Box>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            disableRestoreFocus
            PaperProps={{
              onMouseLeave: handlePopoverClose,
              sx: {
                pointerEvents: 'auto',
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                padding: 1.5,
                minWidth: "min-content",
              }}
              className="day-end-navigator"
            >
              {statuses.map((status, index) => {
                return (
                  <Paper
                    onClick={status.onClick}
                    key={index}
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      borderRadius: "12px",
                      whiteSpace: "nowrap",
                      paddingY: 0.7,
                      paddingX: 1,
                      cursor: "pointer",
                      ...status.styles.buttonStyle,
                      height: "2.3em",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                        opacity: 0.9,
                      }
                    }}
                  >
                    <RenderSvg name={status.icon} customStyles={status.styles.svgStyle}/>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{ fontWeight: 600, fontSize: "0.8em", ...status.styles.textStyle }}
                    >
                      {status.text}
                    </Typography>
                    <ArrowOutward sx={{ fontSize: 10, ...status.styles.textStyle }} />
                  </Paper>
                );
              })}
            </Box>
          </Popover>
        </>
      )}
    </>
  );
}
